import React from 'react';
import { loadFromLocalStorage } from '../../../Common/Validation/LocalStorage';
import DataGrid from '../../../Component/DataGrid/DataGrid';
import { TableColumn } from 'react-data-table-component';
import TransactionService from '../../../Services/TransactionService';
import { amountFormat } from '../../../Component/helper';
import moment from 'moment';
import { IWalletHistoryList, WalletHistoryProps, WalletHistoryState } from './Interface';
import { SyncContext, SyncContextType } from '../../../Contexts/BatchSyncContext';
import UserAndPermissionService from '../../../Services/UserAndPermissionService';
import { ISelectedDateRangeString } from '../../../Component/DataGrid/Interface';
import ReusableDrawer, { IFilters } from '../../../Component/RightDrawer/ReusableDrawer';
import { Texts } from '../../../Component/Reusables/Utility';
import DownloadCsv from '../../../Component/DataGrid/Plugins/DownloadCsv';

class WalletHistory extends React.Component<WalletHistoryProps, WalletHistoryState> {
    static contextType = SyncContext;
    context!: SyncContextType;
    private tableColumns: TableColumn<IWalletHistoryList>[] = [
        {
            name: 'Transaction Id',
            selector: row => row.transactionId,
            sortable: false,
            id: "transactionId",
            minWidth: "120px",
            wrap: true
        },
        {
            name: 'Type',
            selector: row => row.transactionType,
            sortable: true,
            id: "transactionType",
            minWidth: "250px",
            wrap: true
        },
        {
            name: 'Amount',
            selector: row => amountFormat(row.amount, `en-${row.currency.substring(0, 2)}`, row.currency),
            id: "amount",
            right: true,
            cell: (row) => {
                let amount = amountFormat(row.amount, `en-${row.currency.substring(0, 2)}`, row.currency);
                return <span style={row.amount < 0 ? { "color": 'red' } : {}}>{amount}</span>;
            }
        },
        {
            name: 'Opening Balance',
            selector: row => amountFormat(row.openingBalance, `en-${row.currency.substring(0, 2)}`, row.currency),
            id: "openingBalance",
            right: true
        },
        {
            name: 'Closing Balance',
            selector: row => amountFormat(row.closingBalance, `en-${row.currency.substring(0, 2)}`, row.currency),
            id: "closingBalance",
            right: true
        },
        {
            name: 'Date',
            selector: row => moment(row.transactionDate).format(Texts.formatDateTimeDisplay),
            sortable: true,
            id: "transactionDate",
            width: "170px",
            center: true
        },
        {
            name: 'Status',
            selector: row => row.transactionStatus,
            sortable: true,
            id: "transactionStatus",
            center: true
        }
    ]

    constructor(props_: WalletHistoryProps) {
        super(props_);
        let URLParams = new URLSearchParams(window.location.search);
        let transactionId = URLParams.get("id");
        this.state = {
            accessToken: "",
            transactionData: [],
            currentRowsPerPage: 10,
            currentPage: 1,
            totalRows: 100,
            searchText: transactionId ? transactionId : "",
            selectedDateRange:
            {
                startDate: null,
                endDate: null
            },
            contentRef: React.createRef<HTMLDivElement>(),
            csvData: undefined,
            isDrawerOpen: false,
            selectedTxnStatus: "",
            selectedTxnType: "",
            appliedFiltersCount: 0
        }
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken, appliedFiltersCount: 0 }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.userDetails?.isWalletHistoryTabVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        this.getWalletHistory();
                    } else {
                        this.getWalletHistory();
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }



    fetchTxnStatusDropdownOptions = () => {
        return Promise.resolve([{
            statusId: 1, statusName: "In Progress"
        },
        {
            statusId: 2, statusName: "Debited"
        },
        {
            statusId: 3, statusName: "Failed"
        },
        {
            statusId: 4, statusName: "Credited"
        },
        {
            statusId: 5, statusName: "Refund Debited"
        },
        {
            statusId: 6, statusName: "Refund Failed"
        }]);
    };

    fetchTxnTypeDropdownOptions = async () => {
        const response = await TransactionService.fetchWalletTransTypeList(this.state.accessToken);

        if (response?.ok) {
            const result = await response.json();
            console.log(result);
            return result;
        }

        return [];
    };
    handleDrawerApply = (filters: IFilters) => {
        const { startDate, endDate, selectedTxnStatus, selectedTxnType } = filters;

        this.setState(
            {
                selectedDateRange: {
                    startDate: startDate || null,
                    endDate: endDate || null,
                },
                selectedTxnStatus: selectedTxnStatus,
                selectedTxnType: selectedTxnType,
                currentPage: 1
            },
            () => {
                this.getWalletHistory();
            }
        );
    };


    public render() {
        return (
            <div className='body-sub-contianer table-container'>
                <DataGrid
                    title={""}
                    gridId={this.props.gridId}
                    search='Wallet History'
                    isClearDateOnSearch
                    defaultSearch={this.state.searchText}
                    onSearch={(searchText_: string, selectedDateRange: ISelectedDateRangeString | null) => {
                        const newDate = selectedDateRange === null ? { startDate: null, endDate: null } : selectedDateRange;
                        this.setState({
                            searchText: searchText_,
                            currentPage: 1,
                            selectedDateRange: newDate,
                            appliedFiltersCount: searchText_ ? 0 : selectedDateRange !== null ? 1 : this.state.appliedFiltersCount
                        }, () => {
                            this.getWalletHistory();
                        })
                    }}
                    onApply={(filteredDate: { startDate: string, endDate: string }) => {
                        this.setState({
                            selectedDateRange: filteredDate,
                            currentPage: 1
                        }, () => {
                            this.getWalletHistory();
                        })
                    }}
                    columns={this.tableColumns}
                    data={this.state.transactionData}
                    csvData={this.state.csvData}
                    csvFileName={`Wallet History - ${this.props.userName}`}
                    onChangePage={(page: number, totalRows: number) => {
                        this.setState({
                            currentPage: page
                        }, () => {
                            this.getWalletHistory();
                        })
                    }}
                    onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                        this.setState({
                            currentRowsPerPage: currentRowsPerPage_,
                            currentPage: currentPage_
                        }, () => {
                            this.getWalletHistory();
                        })
                    }}
                    pagination
                    paginationTotalRows={this.state.totalRows}
                    exportCsv={true}
                    prepareCSVCustom={this.prepareCSVCustom}
                    parentComponent={this}
                    isDrawerRequired={true}
                    isDrawerOpen={() => this.setState({ isDrawerOpen: true })}
                    appliedFiltersCount={this.state.appliedFiltersCount}
                />
                <ReusableDrawer
                    open={this.state.isDrawerOpen}
                    onClose={() => this.setState({ isDrawerOpen: false })}
                    onApply={this.handleDrawerApply}
                    onFiltersCountChange={(count) => this.setState({ appliedFiltersCount: count })}
                    defaultStartDate={this.state.searchText ? '' : this.state.selectedDateRange.startDate}
                    defaultEndDate={this.state.searchText ? '' : this.state.selectedDateRange.endDate}
                    fetchTxnStatusDropdownOptions={this.fetchTxnStatusDropdownOptions}
                    fetchTxnTypeDropdownOptions={this.fetchTxnTypeDropdownOptions}
                    defaultTxnStatus={this.state.selectedTxnStatus}
                    showCountryFilter={false}
                    showDateRange={true}
                    showTxnStatusFilter={true}
                    showTxnTypeFilter={true}
                />
            </div>
        )
    }

    private getWalletHistory(objDownloadCSV?: DownloadCsv<IWalletHistoryList>) {
        let pageIndex: number = this.state.currentPage;
        let startDate = null, endDate = null;
        if (this.state.selectedDateRange.startDate !== '' && this.state.selectedDateRange.startDate !== "Invalid date" && this.state.selectedDateRange.startDate !== null) {
            startDate = this.state.selectedDateRange.startDate;
            endDate = this.state.selectedDateRange.endDate;
        }
        let postData: any
        postData = {
            userId: this.props.userId,
            startDate: startDate,
            endDate: endDate
        }
        if (!objDownloadCSV) {
            postData.pageNumber = pageIndex;
            postData.recordPerPage = this.state.currentRowsPerPage;
        }
        if (this.state.searchText) {
            postData.search = this.state.searchText;
        }
        if (typeof this.state.selectedTxnStatus === "number") {
            postData.transStatusId = this.state.selectedTxnStatus
        }
        if (typeof this.state.selectedTxnType === "number") {
            postData.transTypeId = this.state.selectedTxnType
        }
        TransactionService.getAdminWalletTransactionHistoryListWithBalance(this.state.accessToken, postData).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    if (objDownloadCSV) {
                        let csvData = resp.list
                        if (csvData.length >= 1) {
                            let _this = objDownloadCSV.props.parentComponent as WalletHistory;
                            _this.setState({
                                csvData: csvData
                            }, () => {
                                objDownloadCSV.prepareCSV();
                            })
                        } else {
                            objDownloadCSV.errormsg();
                        }
                    }
                    else {
                        this.setState({
                            transactionData: resp.list,
                            totalRows: resp.totalRecords
                        })
                    }
                });
            }
        });
    }

    public prepareCSVCustom(objDownloadCSV: DownloadCsv<IWalletHistoryList>) {
        let _this = objDownloadCSV.props.parentComponent as WalletHistory;
        _this.getWalletHistory(objDownloadCSV);
    }
}

export default WalletHistory;